/**
 * SIP registrar accounts for the Address Book.
 *
 * Calls the backend proxy (/api/v1/sip-registrar/accounts), which forwards to
 * the SIP gateway with the server-held key — the gateway key never touches the
 * browser. Mirrors the auth + error handling of sipDial.api.
 */

import { config } from '@/config/env'

const API = config.apiUrl

export class SipRegistrarError extends Error {}

export type RegistrarProtocol = 'sip' | 'h323'

export interface RegistrarAccount {
  aor: string
  username: string
  /** Protocols this account is provisioned for — one device may serve both. */
  protocols: RegistrarProtocol[]
  /** SIP-ENC-P4: 'required' makes dial-out offer SDES-SRTP by default. */
  encryption?: 'required' | 'none'
  /**
   * H.323 registration state. Separate from the SIP fields below because an
   * endpoint registers with ONE gateway — RRQ to the H.323 gatekeeper, or
   * REGISTER to the SIP registrar — so the two have independent answers.
   * null when nothing with this alias has registered over H.323.
   */
  h323: { online: boolean; ipAddress: string | null; endpointId: string | null } | null
  online: boolean
  ipAddress: string | null
  sourcePort: number | null
  transport: string | null
  userAgent: string | null
  ttlSec: number | null
}

function appToken(): string {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') || sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const session = (JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } })
      ?.session
    if (session?.expiresAt && session.expiresAt <= Date.now()) return ''
    return session?.accessToken || ''
  } catch {
    return ''
  }
}

async function fail(res: Response, fallback: string): Promise<never> {
  let message = `${fallback} (${res.status})`
  try {
    const body = await res.json()
    message = body?.error?.message || body?.error || message
  } catch {
    /* keep the status-based fallback */
  }
  throw new SipRegistrarError(message)
}

/** Every provisioned account, with live registration state merged in. */
export async function listAccounts(): Promise<{ realm: string | null; accounts: RegistrarAccount[] }> {
  const token = appToken()
  if (!token) throw new SipRegistrarError('Not signed in')

  const res = await fetch(`${API}/api/v1/sip-registrar/accounts`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) await fail(res, 'Could not load accounts')
  return res.json()
}

/**
 * Provision an account an endpoint can then register against.
 *
 * `protocol` is merged by the gateway rather than replacing what the account
 * already has, so adding an existing username for the other protocol makes it
 * serve both.
 */
export async function createAccount(
  username: string,
  secret: string,
  protocol: RegistrarProtocol,
  /**
   * SIP-ENC-P4: whether a dial-out to this endpoint offers encrypted media.
   * Omit to leave an existing account's setting untouched; the gateway
   * defaults a new account to 'none'.
   */
  encryption?: 'required' | 'none',
): Promise<void> {
  const token = appToken()
  if (!token) throw new SipRegistrarError('Not signed in')

  const res = await fetch(`${API}/api/v1/sip-registrar/accounts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(
      encryption === undefined
        ? { username, secret, protocol }
        : { username, secret, protocol, encryption },
    ),
  })
  if (!res.ok) await fail(res, 'Could not create account')
}
